import { grey } from "@mui/material/colors";
import { createTheme, ThemeOptions } from "@mui/material/styles";
// this insures DataGrid types are imported correctly
import type {} from "@mui/x-data-grid/themeAugmentation";

import { Brand, getConfig } from "merchant-portal-frontend/src/config";

import { breadTheme } from "./BreadTheme.config";
import { white } from "./colors";
import { rbcTheme } from "./RBCTheme.config";

export const getGlobalTheme = () => {
	const currentBrand = getConfig<Brand>("brand", {
		safeReturn: true,
		defaultValue: Brand.Bread,
	});

	const tenantTheme = currentBrand === Brand.Rbc ? rbcTheme : breadTheme;

	const globalTheme: ThemeOptions = {
		typography: {
			fontFamily: `Poppins, Helvetica, Arial, sans-serif`,
			fontSize: 22.5, // this is huge because of the font-size of 62.5% in GlobalStyle.ts
		},
		components: {
			MuiButtonBase: {
				defaultProps: {
					disableRipple: true,
				},
			},
			MuiButton: {
				styleOverrides: {
					root: {
						"margin": "0.5rem",
						"lineHeight": "2.6rem",
						"textTransform": "none",
						"borderRadius": "5rem",
						"padding": "1.2rem 4rem",
						"fontWeight": "700",
						"fontSize": "1.6rem",
						"boxShadow": "none",
						":hover": {
							transition: "none",
							cursor: "pointer",
							boxShadow: "none",
						},
						"transition": "none",
						"&.Mui-focusVisible": {
							boxShadow: "none",
						},
					},
					outlined: {
						"backgroundColor": "transparent",
						"transition": "none",
						":hover": {
							transition: "none",
							backgroundColor: "transparent",
						},
						"&.Mui-focusVisible": {
							padding: "1.1rem 3.9rem",
						},
					},
					contained: {
						"&.Mui-focusVisible": {
							padding: "1rem 3.8rem",
						},
					},
					text: {
						"alignSelf": "center",
						"borderRadius": 0,
						"padding": "0 0 0.2rem",
						"height": "2.8rem",
						":hover": {
							backgroundColor: "transparent",
							paddingBottom: "0.1rem",
						},
						":active": {
							border: 0,
							paddingBottom: "0.3rem",
						},
						"&.Mui-focusVisible": {
							padding: "0 0.2rem 0.2rem 0.2rem",
						},
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						"boxShadow": "none",
						"border": `1px solid ${grey[300]}`,
						"&.MuiAccordion-root": {
							borderTop: 0,
							borderLeft: 0,
							borderRight: 0,
						},
					},
				},
			},
			MuiBackdrop: {
				styleOverrides: {
					root: {
						backgroundColor: white,
					},
					invisible: {
						backgroundColor: "transparent",
					},
				},
			},
			MuiList: {
				styleOverrides: {
					root: {
						paddingTop: 0,
						paddingBottom: 0,
					},
				},
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						backgroundColor: white,
						border: `1px solid ${grey[300]}`,
					},
					arrow: {
						color: grey[300],
					},
				},
			},
			MuiIconButton: {
				styleOverrides: {
					root: {
						"&.Mui-disabled": {
							padding: "0.6rem",
						},
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						backgroundColor: white,
						borderRadius: "0.2rem",
						height: "4rem",
					},
					input: {
						textOverflow: "ellipsis",
						overflow: "hidden",
						whiteSpace: "nowrap",
						padding: "1rem",
						fontSize: "1.6rem",
					},
				},
			},
			MuiInputAdornment: {
				styleOverrides: {
					root: {
						height: "3.8rem",
						maxHeight: "unset",
					},
					positionStart: {
						marginLeft: "-1.3rem",
						marginRight: 0,
					},
					positionEnd: {
						marginRight: "-1.3rem",
					},
				},
			},
			MuiAutocomplete: {
				styleOverrides: {
					option: {
						fontSize: "1.4rem",
					},
					endAdornment: {
						top: "inherit",
						transform: "unset",
						button: {
							"&.Mui-disabled": {
								border: 0,
							},
						},
					},
				},
			},
			MuiDataGrid: {
				styleOverrides: {
					root: {
						"borderRadius": "0.4rem",
						"& .MuiDataGrid-cell.overflow-unset-on-hover": {
							"&:hover": {
								overflow: "unset",
							},
						},
					},
					columnHeaderTitle: {
						fontWeight: "bold",
					},
				},
			},
			MuiSelect: {
				styleOverrides: {
					root: {
						height: "4rem",
						marginTop: "0.4rem", // TODO: this should eventually be removed, but right now a lot of our Selects depend on it
						width: "100%",
					},
				},
			},
			MuiFormControlLabel: {
				styleOverrides: {
					label: {
						fontSize: "1.4rem",
					},
				},
			},
			MuiAppBar: {
				styleOverrides: {
					root: {
						border: 0,
						backgroundColor: "white",
					},
				},
			},
			MuiFormLabel: {
				styleOverrides: {
					root: {
						fontSize: "1.4rem",
					},
				},
			},
			MuiAccordionSummary: {
				styleOverrides: {
					root: {
						"& .MuiAccordionSummary-expandIconWrapper": {
							"borderRadius": "5rem",
							"&.Mui-disabled": {
								padding: "0.6rem",
							},
						},
					},
				},
			},
			MuiModal: {
				styleOverrides: {
					root: {
						zIndex: "inherit",
					},
				},
			},
			MuiTable: {
				styleOverrides: {
					root: {
						borderCollapse: "separate",
						borderSpacing: 0,
					},
				},
			},
			MuiTableCell: {
				styleOverrides: {
					root: {
						fontSize: "1.2rem",
					},
				},
			},
			MuiToggleButtonGroup: {
				styleOverrides: {
					root: {
						height: "4rem",
						padding: "0 0.5rem",
						alignItems: "center",
						borderRadius: "3rem",
					},
					firstButton: {
						borderRadius: "3rem",
					},
					middleButton: {
						borderRadius: "3rem",
					},
					lastButton: {
						borderRadius: "3rem",
					},
				},
			},
			MuiToggleButton: {
				styleOverrides: {
					root: {
						"fontSize": "1.2rem",
						"textTransform": "none",
						"fontWeight": "bold",
						"border": "none",
						"height": "3rem",
						"borderRadius": "3rem",
						"&.Mui-selected": {
							zIndex: 1,
							boxShadow:
								"-4px 4px 4px rgba(0, 0, 0, 0.1), 4px 0px 4px rgba(0, 0, 0, 0.1), 0px -4px 4px rgba(0, 0, 0, 0.05), 0px 4px 8px rgba(0, 0, 0, 0.2)",
						},
					},
				},
			},
			MuiTabs: {
				styleOverrides: {
					indicator: {
						height: "3px",
					},
				},
			},
			MuiTab: {
				styleOverrides: {
					root: {
						textTransform: "none",
						fontWeight: "bold",
					},
				},
			},
		},
	};

	return createTheme(globalTheme, tenantTheme);
};
